import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Editor from '@monaco-editor/react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Code2, Eye } from 'lucide-react';
import { Button } from '@/shared/components/ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/shared/components/ui/tooltip';
import { useTheme } from '@/shared/hooks/use-theme';
import { cn } from '@/infrastructure/lib/utils';
import { markdownComponents } from './markdownComponents';
import { INITIAL_VIEW, lengthStatus, nextView, toggleIcon, visibleView, type MarkdownView } from './markdownFieldView';

interface InlineMarkdownFieldProps {
  value: string;
  onChange?: (value: string) => void;
  /** Sem permissão de edição: só a visão renderizada, sem o botão de alternar. */
  readOnly?: boolean;
  /** Mostra o contador e destaca quando o texto passa do limite. Não corta o texto. */
  maxLength?: number;
  /** Altura do editor de código. A renderizada cresce com o conteúdo. */
  editorHeight?: number | string;
  className?: string;
  /** Ações extras ao lado do botão de alternar (histórico, cadeado...). */
  actions?: React.ReactNode;
  /**
   * Visão controlada por quem usa o campo: o botão de alternar fica na barra de
   * quem chama (`MarkdownViewToggle`), e o campo aparece sem moldura nem barra própria.
   */
  view?: MarkdownView;
}

interface MarkdownViewToggleProps {
  view: MarkdownView;
  onChange: (view: MarkdownView) => void;
  className?: string;
}

/** O botão que alterna as visões: na renderizada mostra `<>`, no código mostra o olho. */
export const MarkdownViewToggle = ({ view, onChange, className }: MarkdownViewToggleProps) => {
  const { t } = useTranslation();
  const label = view === 'rendered' ? t('common.markdown.showCode') : t('common.markdown.showRendered');
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className={cn('h-7 w-7 p-0', className)}
            aria-label={label}
            onClick={() => onChange(nextView(view))}
          >
            {toggleIcon(view) === 'code' ? <Code2 className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
          </Button>
        </TooltipTrigger>
        <TooltipContent>
          <p className="text-xs">{label}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

/**
 * Campo de markdown editado no próprio lugar (AB#86817, AC10): abre renderizado,
 * `<>` leva ao código no Monaco e o olho volta. Usado pela Soul e pelos blocos de
 * texto da Mind.
 */
export const InlineMarkdownField = ({
  value,
  onChange,
  readOnly = false,
  maxLength,
  editorHeight = 360,
  className,
  actions,
  view: controlledView,
}: InlineMarkdownFieldProps) => {
  const { t } = useTranslation();
  const { theme } = useTheme();
  const [ownView, setOwnView] = useState<MarkdownView>(INITIAL_VIEW);
  const controlled = controlledView !== undefined;

  const shown = visibleView(controlled ? controlledView : ownView, readOnly);
  const status = maxLength ? lengthStatus(value, maxLength) : null;

  return (
    <div className={cn(!controlled && 'rounded-md border bg-background', className)}>
      {!controlled && (
      <div className="flex items-center justify-end gap-1 border-b bg-muted/30 px-2 py-1">
        {status && (
          <span
            className={cn('mr-auto px-1 text-xs', status.isOver ? 'text-destructive' : 'text-muted-foreground')}
            data-testid="markdown-length"
          >
            {status.isOver
              ? t('common.markdown.overLimit', { count: status.over })
              : t('common.markdown.characters', { count: status.count, max: status.max })}
          </span>
        )}
        {actions}
        {!readOnly && <MarkdownViewToggle view={shown} onChange={setOwnView} />}
      </div>
      )}

      {shown === 'code' ? (
        <Editor
          height={editorHeight}
          language="markdown"
          value={value}
          onChange={(next) => onChange?.(next ?? '')}
          theme={theme === 'dark' ? 'vs-dark' : 'vs'}
          options={{
            minimap: { enabled: false },
            wordWrap: 'on',
            fontSize: 13,
            lineHeight: 20,
            scrollBeyondLastLine: false,
            padding: { top: 12 },
            lineNumbers: 'off',
            glyphMargin: false,
            folding: false,
            renderLineHighlight: 'none',
            overviewRulerLanes: 0,
            hideCursorInOverviewRuler: true,
            scrollbar: { vertical: 'auto', horizontal: 'hidden' },
          }}
        />
      ) : (
        <div className={cn('prose prose-sm max-w-none dark:prose-invert', !controlled && 'px-5 py-4')}>
          {value.trim() ? (
            <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
              {value}
            </ReactMarkdown>
          ) : (
            <p className="text-sm text-muted-foreground">{t('common.markdown.empty')}</p>
          )}
        </div>
      )}
    </div>
  );
};
